<template>
  <Teleport to="body">
    <div v-if="modelValue" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>
            {{ isEditing ? s__('TaskTemplates|Edit Template') : s__('TaskTemplates|New Template') }}
          </h3>
          <button type="button" class="modal-close" aria-label="Close" @click="handleClose">
            ×
          </button>
        </div>
        <form @submit.prevent="handleSubmit">
          <div class="modal-body">
            <!-- Task Title -->
            <div class="form-row">
              <label>{{ s__('TaskTemplates|Task Title') }} *</label>
              <SingleLineTextarea
                v-model="form.task_title"
                :rows-desktop="1"
                :rows-mobile="2"
                :placeholder="s__('TaskTemplates|Default task title')"
                required
              />
            </div>

            <!-- Task Description -->
            <div class="form-row">
              <label>{{ s__('TaskTemplates|Task Description') }}</label>
              <TelegramDualEditor
                v-model="form.task_description"
                :members="users"
                :project-id="projectId"
                :enable-wysiwyg="wysiwygEnabled"
                :clipboard-upload-enabled="s3AttachmentsEnabled"
                :attachment-context="taskDescriptionAttachmentContext"
                :attachment-draft-id="attachmentDraftId"
                :placeholder="s__('TaskTemplates|Default task description')"
                min-height="130px"
                @draft-created="attachmentDraftId = $event"
              />
              <div style="display: flex; gap: 8px; margin-top: 8px">
                <button
                  type="button"
                  class="btn-sm btn-secondary"
                  :disabled="!form.task_description?.trim()"
                  @click.prevent.stop="showTaskDescriptionPreview"
                >
                  {{ s__('TaskTemplates|Preview') }}
                </button>
              </div>
            </div>

            <div v-if="scope === 'project' && s3AttachmentsEnabled" class="form-row">
              <DraftAttachmentUploader
                v-model:attachment-draft-id="attachmentDraftId"
                v-model:attachments="draftAttachments"
                :project-id="projectId"
                purpose="task_template_attachments"
                :label="s__('Attachments|Attachments')"
                :drop-text="s__('Attachments|Drop files here or click to upload')"
                :size-text="s__('Attachments|Maximum file size: 100 MB')"
                :uploading-text="s__('Attachments|Uploading...')"
                :upload-error-text="s__('Attachments|Failed to upload attachments')"
                :delete-error-text="s__('Attachments|Failed to delete attachment')"
                :delete-title="s__('Attachments|Delete attachment')"
              />
            </div>

            <div
              v-if="
                scope === 'project' && s3AttachmentsEnabled && availableProjectAttachments.length
              "
              class="form-row"
            >
              <ProjectAttachmentPicker
                :available-attachments="availableProjectAttachments"
                :selected-ids="selectedExistingAttachmentIds"
                :label="s__('Attachments|Attach existing project file')"
                :placeholder="s__('Attachments|Select a file…')"
                :delete-title="s__('Attachments|Delete attachment')"
                select-id="template-existing-project-attachment-select"
                @update:selected-ids="selectedExistingAttachmentIds = $event"
              />
            </div>

            <!-- Assignee and Priority -->
            <div class="form-row form-row-split">
              <div style="flex: 1">
                <label>{{ s__('TaskTemplates|Default Assignee') }}</label>
                <UserSelect
                  v-model="form.default_assignee_id"
                  :users="users"
                  :project-id="projectId"
                  :placeholder="s__('TaskTemplates|Select assignee')"
                  :show-empty-option="true"
                />
              </div>
              <div style="flex: 1">
                <label>{{ s__('TaskTemplates|Default Priority') }}</label>
                <select
                  v-model="form.default_priority"
                  :key="`priority-select-${form.default_priority || 'none'}`"
                >
                  <option :value="null">{{ s__('TaskTemplates|None') }}</option>
                  <option v-for="opt in priorityOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Category and Status (only for project templates) -->
            <div v-if="scope === 'project'" class="form-row form-row-split">
              <div style="flex: 1">
                <label>{{ s__('TaskTemplates|Default Category') }}</label>
                <select v-model="form.default_task_category_id" @change="handleCategoryChange">
                  <option :value="null">{{ s__('TaskTemplates|None') }}</option>
                  <option v-for="cat in taskCategories" :key="cat.id" :value="cat.id">
                    {{ cat.name }}
                  </option>
                </select>
              </div>
              <div style="flex: 1">
                <label>{{ s__('TaskTemplates|Default Status') }}</label>
                <select
                  v-model="form.default_workflow_status_id"
                  :disabled="!form.default_task_category_id"
                >
                  <option :value="null">{{ s__('TaskTemplates|None') }}</option>
                  <option
                    v-for="status in availableWorkflowStatuses"
                    :key="status.id"
                    :value="status.id"
                  >
                    {{ status.name
                    }}{{ status.isInitial ? ` (${s__('TaskTemplates|Initial')})` : '' }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Parent Task (optional, can be added later if needed) -->
            <!-- <div class="form-row">
              <label>{{ s__('TaskTemplates|Default Parent Task') }}</label>
              <select v-model="form.default_parent_task_id">
                <option :value="null">{{ s__('TaskTemplates|None') }}</option>
              </select>
            </div> -->

            <!-- Labels -->
            <div v-if="scope === 'project'" class="form-row">
              <label>{{ s__('TaskTemplates|Project Labels') }}</label>
              <MultiSelect
                v-if="projectLabels.length > 0"
                :options="projectLabels"
                :selected-ids="form.default_labels"
                :label="''"
                :placeholder="s__('TaskTemplates|Select labels...')"
                :search-placeholder="s__('TaskTemplates|Search labels...')"
                :no-results-text="s__('TaskTemplates|No labels found')"
                @update:selected-ids="form.default_labels = $event"
              />
              <div v-else class="muted">{{ s__('TaskTemplates|No project labels available') }}</div>
            </div>

            <div v-if="scope === 'user'" class="form-row">
              <label>{{ s__('TaskTemplates|Personal Labels') }}</label>
              <MultiSelect
                v-if="personalLabels.length > 0"
                :options="personalLabels"
                :selected-ids="form.default_personal_labels"
                :label="''"
                :placeholder="s__('TaskTemplates|Select labels...')"
                :search-placeholder="s__('TaskTemplates|Search labels...')"
                :no-results-text="s__('TaskTemplates|No labels found')"
                @update:selected-ids="form.default_personal_labels = $event"
              />
              <div v-else class="muted">
                {{ s__('TaskTemplates|No personal labels available') }}
              </div>
            </div>

            <!-- Confidential -->
            <div class="form-row">
              <label>
                <input
                  v-model="form.default_confidential"
                  type="checkbox"
                  style="margin-right: 8px"
                />
                {{ s__('TaskTemplates|Default Confidential') }}
              </label>
            </div>

            <!-- Checklist (always available for templates) -->
            <div class="form-row">
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 8px;
                "
              >
                <label style="margin: 0">{{ s__('TaskTemplates|Checklist') }}</label>
                <button
                  v-if="checklistForm.items.length > 0"
                  type="button"
                  class="btn-icon-small"
                  :title="s__('TaskTemplates|Delete checklist')"
                  @click="clearChecklist"
                >
                  <TrashIcon />
                </button>
              </div>
              <div class="checklist-items-form">
                <div
                  v-for="(item, index) in checklistForm.items"
                  :key="index"
                  class="checklist-item-row"
                  style="display: flex; gap: 8px; align-items: center; margin-bottom: 4px"
                >
                  <span style="color: #757575; font-size: 0.9rem; min-width: 24px"
                    >{{ index + 1 }}.</span
                  >
                  <input
                    v-model="checklistForm.items[index].title"
                    type="text"
                    :placeholder="s__('TaskTemplates|Item title')"
                    style="flex: 1"
                  />
                  <button
                    type="button"
                    class="btn-icon-small"
                    :title="s__('TaskTemplates|Remove item')"
                    @click="removeChecklistItem(index)"
                  >
                    <TrashIcon />
                  </button>
                </div>
                <div style="display: flex; gap: 8px; align-items: center; margin-top: 8px">
                  <input
                    ref="newChecklistItemInput"
                    v-model="newChecklistItemTitle"
                    type="text"
                    :placeholder="s__('TaskTemplates|Add checklist item')"
                    style="flex: 1"
                    @keydown.enter="handleChecklistItemEnter"
                    @keydown.tab="handleChecklistItemTab"
                    @keydown.esc.prevent="cancelChecklistItemCreation"
                  />
                  <button
                    type="button"
                    class="btn-sm"
                    :disabled="!newChecklistItemTitle.trim()"
                    @click="addChecklistItem"
                  >
                    {{ s__('TaskTemplates|Add') }}
                  </button>
                </div>
              </div>
              <div class="muted">
                {{
                  scope === 'user'
                    ? s__(
                        'TaskTemplates|Optional: Add a checklist. It will be applied only if the project has checklists enabled.'
                      )
                    : s__('TaskTemplates|Optional: Add a checklist to track task completion steps')
                }}
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="error" class="error" style="margin-top: 12px">{{ error }}</div>

            <!-- Action Buttons -->
            <div
              class="mobile-stretch-btn-group"
              style="display: flex; gap: 8px; margin-top: 16px; justify-content: flex-end"
            >
              <button type="button" class="btn-secondary" @click="handleClose">
                {{ __('Cancel') }}
              </button>
              <button
                type="submit"
                :disabled="!canSubmit || creating || updating"
                class="btn-primary"
              >
                {{
                  creating
                    ? s__('TaskTemplates|Creating...')
                    : updating
                      ? s__('TaskTemplates|Saving...')
                      : isEditing
                        ? s__('TaskTemplates|Save')
                        : s__('TaskTemplates|Create')
                }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Task Description Preview Modal -->
    <div v-if="showTaskDescriptionPreviewModal" class="modal-overlay">
      <div class="modal-content" style="max-width: 600px">
        <div class="modal-header">
          <h3>{{ s__('TaskTemplates|Description Preview') }}</h3>
          <button
            type="button"
            class="modal-close"
            aria-label="Close"
            @click="showTaskDescriptionPreviewModal = false"
          >
            ×
          </button>
        </div>
        <div class="modal-body">
          <div class="rich-content-read rich-content-preview">
            <div class="rich-content-read" v-html="taskDescriptionPreviewContent"></div>
            <div
              v-if="!taskDescriptionPreviewContent"
              class="muted"
              style="color: #9e9e9e; font-style: italic"
            >
              {{ s__('TaskTemplates|No content to preview') }}
            </div>
          </div>
          <div style="display: flex; justify-content: flex-end; margin-top: 16px">
            <button class="btn-sm btn-secondary" @click="showTaskDescriptionPreviewModal = false">
              {{ s__('TaskTemplates|Close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useQuery, useLazyQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __ } from '@/locale'
import UserSelect from './UserSelect.vue'
import MultiSelect from './MultiSelect.vue'
import TelegramDualEditor from './TelegramDualEditor.vue'
import SingleLineTextarea from './SingleLineTextarea.vue'
import DraftAttachmentUploader from './DraftAttachmentUploader.vue'
import ProjectAttachmentPicker from './ProjectAttachmentPicker.vue'
import TrashIcon from './icons/TrashIcon.vue'
import { useBodyScrollLock } from '../../composables/useBodyScrollLock.js'
import { telegramMarkdownToHtml } from '../../utils/telegramEditorCodec.js'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  scope: {
    type: String,
    required: true,
    validator: value => ['project', 'user'].includes(value),
  },
  projectId: {
    type: String,
    default: null,
  },
  template: {
    type: Object,
    default: null,
  },
  projectLabels: {
    type: Array,
    default: () => [],
  },
  personalLabels: {
    type: Array,
    default: () => [],
  },
  users: {
    type: Array,
    default: () => [],
  },
  taskCategories: {
    type: Array,
    default: () => [],
  },
  creating: {
    type: Boolean,
    default: false,
  },
  updating: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'submit', 'close'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.modelValue))

const isEditing = computed(() => !!props.template)
const wysiwygEnabled = computed(() => window.gon?.wysiwyg_editor_enabled !== false)
const s3AttachmentsEnabled = computed(() => window.gon?.s3_attachments_enabled === true)
const attachmentDraftId = ref(null)
const draftAttachments = ref([])
const selectedExistingAttachmentIds = ref([])
const taskDescriptionAttachmentContext = computed(() => {
  if (props.template?.id) {
    return { attachableType: 'taskTemplate', attachableId: props.template.id }
  }

  return {
    projectId: props.scope === 'project' ? props.projectId : null,
    purpose: 'task_template_description',
  }
})

// Form state
const form = ref({
  description: '',
  task_title: '',
  task_description: '',
  default_assignee_id: null,
  default_priority: null,
  default_task_category_id: null,
  default_workflow_status_id: null,
  default_parent_task_id: null,
  default_labels: [],
  default_personal_labels: [],
  default_confidential: false,
  checklist_data: null,
})

// Checklist form state
const checklistForm = ref({
  items: [],
})
const newChecklistItemTitle = ref('')
const newChecklistItemInput = ref(null)
const checklistsEnabled = ref(false)

// Preview modal state
const showTaskDescriptionPreviewModal = ref(false)
const taskDescriptionPreviewContent = ref('')

// Priority options
const priorityOptions = ref([
  { value: 'lowest', label: s__('Priority|Lowest') },
  { value: 'low', label: s__('Priority|Low') },
  { value: 'medium', label: s__('Priority|Medium') },
  { value: 'high', label: s__('Priority|High') },
  { value: 'highest', label: s__('Priority|Highest') },
])

// Workflow statuses (filtered by selected category)
const availableWorkflowStatuses = ref([])

// GraphQL queries for fetching data
const PROJECT_DATA_QUERY = gql`
  query ProjectDataForTemplate($id: ID!) {
    project(id: $id) {
      id
      labels {
        id
        title
        color
      }
      members {
        id
        username
        name
      }
      taskCategories {
        id
        name
        workflow {
          id
          name
          statuses {
            id
            name
            position
            isInitial
          }
        }
      }
      checklistsEnabled
      attachments(first: 50, projectOnly: true) {
        edges {
          node {
            id
            filename
            humanSize
          }
        }
      }
    }
    priorityOptions {
      value
      label
    }
  }
`

const PERSONAL_LABELS_QUERY = gql`
  query PersonalLabels {
    personalLabels {
      id
      title
      color
    }
  }
`

const USER_DATA_QUERY = gql`
  query UserDataForTemplate {
    currentUser {
      id
      projects(first: 100) {
        edges {
          node {
            id
            isPersonal
            taskCategories {
              id
              name
              workflow {
                id
                name
                statuses {
                  id
                  name
                  position
                  isInitial
                }
              }
            }
            members {
              id
              username
              name
            }
            checklistsEnabled
          }
        }
      }
    }
    priorityOptions {
      value
      label
    }
  }
`

// Fetch project data if project scope
const { result: projectDataResult, loading: projectDataLoading } = useQuery(
  PROJECT_DATA_QUERY,
  () => ({ id: props.projectId }),
  {
    enabled: computed(() => props.modelValue && props.scope === 'project' && !!props.projectId),
  }
)

// Fetch user data if user scope
const { result: userDataResult, loading: userDataLoading } = useQuery(
  USER_DATA_QUERY,
  {},
  {
    enabled: computed(() => props.modelValue && props.scope === 'user'),
  }
)

// Fetch personal labels if user scope
const {
  load: loadPersonalLabels,
  result: personalLabelsResult,
  loading: personalLabelsLoading,
} = useLazyQuery(PERSONAL_LABELS_QUERY)

// Helper function to initialize form from template
function initializeFormFromTemplate() {
  if (!props.template) return

  const priority = props.template.defaultPriority || props.template.default_priority
  console.log('[CreateTaskTemplateModal] Initializing form from template')
  console.log('[CreateTaskTemplateModal] Raw priority value:', priority, typeof priority)
  console.log('[CreateTaskTemplateModal] Template object:', props.template)

  let convertedPriority = null
  if (priority != null && priority !== undefined && priority !== '') {
    // GraphQL returns enum name in uppercase (e.g., "MEDIUM")
    // priorityOptions might have uppercase or lowercase values depending on when GraphQL data loads
    // Convert to lowercase and find matching option (case-insensitive)
    const converted = String(priority).toLowerCase()
    console.log('[CreateTaskTemplateModal] Converted priority:', converted)

    // Get available options (they might be uppercase from GraphQL or lowercase from initial state)
    const validOptions = priorityOptions.value.map(o => o.value)
    console.log('[CreateTaskTemplateModal] Valid options:', validOptions)

    // Try exact match first (case-sensitive)
    if (validOptions.includes(converted)) {
      convertedPriority = converted
      console.log('[CreateTaskTemplateModal] Priority matched (exact):', convertedPriority)
    } else {
      // Try case-insensitive match
      const match = validOptions.find(opt => opt.toLowerCase() === converted.toLowerCase())
      if (match) {
        // Use the actual option value (might be uppercase)
        convertedPriority = match
        console.log(
          '[CreateTaskTemplateModal] Priority matched (case-insensitive):',
          convertedPriority
        )
      } else {
        convertedPriority = null
        console.log('[CreateTaskTemplateModal] No match found for priority')
      }
    }
  } else {
    console.log('[CreateTaskTemplateModal] Priority is null/undefined/empty')
  }

  console.log('[CreateTaskTemplateModal] Final converted priority:', convertedPriority)

  form.value = {
    description: props.template.description || '',
    task_title: props.template.taskTitle || props.template.task_title || '',
    task_description: props.template.taskDescription || props.template.task_description || '',
    default_assignee_id:
      props.template.defaultAssigneeId || props.template.default_assignee_id || null,
    default_priority: convertedPriority,
    default_task_category_id:
      props.template.defaultTaskCategoryId || props.template.default_task_category_id || null,
    default_workflow_status_id:
      props.template.defaultWorkflowStatusId || props.template.default_workflow_status_id || null,
    default_parent_task_id:
      props.template.defaultParentTaskId || props.template.default_parent_task_id || null,
    default_labels: (props.template.labels || []).map(l => String(l.id)),
    default_personal_labels: (
      props.template.personalLabels ||
      props.template.personal_labels ||
      []
    ).map(l => String(l.id)),
    default_confidential:
      props.template.defaultConfidential ?? props.template.default_confidential ?? false,
    checklist_data: null,
  }
  attachmentDraftId.value = null
  draftAttachments.value = []
  selectedExistingAttachmentIds.value = (props.template.attachments || []).map(attachment =>
    String(attachment.id)
  )

  // Initialize checklist form from template
  if (props.template.checklistData || props.template.checklist_data) {
    const checklistData = props.template.checklistData || props.template.checklist_data
    checklistForm.value.items =
      (checklistData.items || []).map(item => ({
        title: item.title || '',
      })) || []
  } else {
    checklistForm.value = { items: [] }
  }
  newChecklistItemTitle.value = ''

  console.log('[CreateTaskTemplateModal] Form value after initialization:', form.value)
  console.log('[CreateTaskTemplateModal] Form default_priority:', form.value.default_priority)

  // Update workflow statuses based on selected category (only for project templates)
  if (props.scope === 'project' && form.value.default_task_category_id) {
    handleCategoryChange()
  }

  // Use nextTick to ensure the select element is rendered before setting the value
  nextTick(() => {
    console.log(
      '[CreateTaskTemplateModal] After nextTick - form.default_priority:',
      form.value.default_priority
    )
    // The form value should already be set, but let's verify it's still there
    if (form.value.default_priority !== convertedPriority) {
      console.log('[CreateTaskTemplateModal] Priority mismatch detected, fixing...')
      form.value.default_priority = convertedPriority
    }
  })
}

// Watch for modal opening to fetch data
watch(
  () => props.modelValue,
  isOpen => {
    if (isOpen) {
      // Initialize form from template if editing
      if (props.template) {
        initializeFormFromTemplate()
      } else {
        // Reset form for new template
        form.value = {
          description: '',
          task_title: '',
          task_description: '',
          default_assignee_id: null,
          default_priority: null,
          default_task_category_id: null,
          default_workflow_status_id: null,
          default_parent_task_id: null,
          default_labels: [],
          default_personal_labels: [],
          default_confidential: false,
          checklist_data: null,
        }
        attachmentDraftId.value = null
        draftAttachments.value = []
        selectedExistingAttachmentIds.value = []
        checklistForm.value = { items: [] }
        newChecklistItemTitle.value = ''
        availableWorkflowStatuses.value = []
      }

      // Load personal labels if user scope
      if (props.scope === 'user' && !personalLabelsResult.value && !personalLabelsLoading.value) {
        loadPersonalLabels()
      }
    } else {
      // Reset form when modal closes
      form.value = {
        description: '',
        task_title: '',
        task_description: '',
        default_assignee_id: null,
        default_priority: null,
        default_task_category_id: null,
        default_workflow_status_id: null,
        default_parent_task_id: null,
        default_labels: [],
        default_personal_labels: [],
        default_confidential: false,
        checklist_data: null,
      }
      attachmentDraftId.value = null
      draftAttachments.value = []
      selectedExistingAttachmentIds.value = []
      checklistForm.value = { items: [] }
      newChecklistItemTitle.value = ''
      availableWorkflowStatuses.value = []
    }
  }
)

// Also watch for template prop changes (in case template is set after modal opens)
watch(
  () => props.template,
  newTemplate => {
    if (props.modelValue && newTemplate) {
      initializeFormFromTemplate()
    }
  },
  { deep: true }
)

// Watch project data result
watch(
  projectDataResult,
  data => {
    if (data?.priorityOptions) {
      // GraphQL returns uppercase values (e.g., "MEDIUM"), convert to lowercase for form consistency
      priorityOptions.value = data.priorityOptions.map(opt => ({
        value: String(opt.value).toLowerCase(),
        label: opt.label,
      }))
    }
    if (data?.project?.checklistsEnabled !== undefined) {
      checklistsEnabled.value = data.project.checklistsEnabled
    }
  },
  { immediate: true }
)

// Watch user data result
watch(
  userDataResult,
  data => {
    if (data?.priorityOptions) {
      // GraphQL returns uppercase values (e.g., "MEDIUM"), convert to lowercase for form consistency
      priorityOptions.value = data.priorityOptions.map(opt => ({
        value: String(opt.value).toLowerCase(),
        label: opt.label,
      }))
    }
    // Check if personal project has checklists enabled
    if (data?.currentUser?.projects?.edges) {
      const personalProject = data.currentUser.projects.edges.find(edge => edge.node.isPersonal)
      if (personalProject?.node?.checklistsEnabled !== undefined) {
        checklistsEnabled.value = personalProject.node.checklistsEnabled
      }
    }
  },
  { immediate: true }
)

// Watch personal labels result
watch(
  personalLabelsResult,
  data => {
    // Personal labels are passed as props, but we can update them if needed
  },
  { immediate: true }
)

// Computed for available data
const projectLabels = computed(() => {
  if (props.scope === 'project' && projectDataResult.value?.project?.labels) {
    return projectDataResult.value.project.labels
  }
  return props.projectLabels || []
})

const users = computed(() => {
  if (props.scope === 'project' && projectDataResult.value?.project?.members) {
    return projectDataResult.value.project.members
  }
  if (props.scope === 'user' && userDataResult.value?.currentUser?.projects?.edges) {
    // Find personal project and get its members
    const personalProject = userDataResult.value.currentUser.projects.edges.find(
      edge => edge.node.isPersonal
    )
    if (personalProject?.node?.members) {
      return personalProject.node.members
    }
  }
  return props.users || []
})

const taskCategories = computed(() => {
  if (props.scope === 'project' && projectDataResult.value?.project?.taskCategories) {
    return projectDataResult.value.project.taskCategories
  }
  if (props.scope === 'user' && userDataResult.value?.currentUser?.projects?.edges) {
    // Find personal project and get its task categories
    const personalProject = userDataResult.value.currentUser.projects.edges.find(
      edge => edge.node.isPersonal
    )
    if (personalProject?.node?.taskCategories) {
      return personalProject.node.taskCategories
    }
  }
  return props.taskCategories || []
})

const personalLabels = computed(() => {
  if (props.scope === 'user' && personalLabelsResult.value?.personalLabels) {
    return personalLabelsResult.value.personalLabels
  }
  return props.personalLabels || []
})

const availableProjectAttachments = computed(() => {
  if (props.scope !== 'project') return []

  const projectAttachments = (projectDataResult.value?.project?.attachments?.edges || [])
    .map(edge => edge.node)
    .filter(Boolean)
  const templateAttachments = props.template?.attachments || []
  const allAttachments = [...projectAttachments, ...templateAttachments]
  const byId = new Map()

  allAttachments.forEach(attachment => {
    byId.set(String(attachment.id), attachment)
  })

  return Array.from(byId.values())
})

function handleCategoryChange() {
  if (!form.value.default_task_category_id) {
    availableWorkflowStatuses.value = []
    form.value.default_workflow_status_id = null
    return
  }

  const category = taskCategories.value.find(cat => cat.id === form.value.default_task_category_id)

  if (category?.workflow?.statuses) {
    availableWorkflowStatuses.value = [...category.workflow.statuses].sort(
      (a, b) => a.position - b.position
    )

    // Auto-select initial status if available
    const initialStatus = availableWorkflowStatuses.value.find(s => s.isInitial)
    if (initialStatus) {
      form.value.default_workflow_status_id = initialStatus.id
    } else if (availableWorkflowStatuses.value.length > 0) {
      form.value.default_workflow_status_id = availableWorkflowStatuses.value[0].id
    } else {
      form.value.default_workflow_status_id = null
    }
  } else {
    availableWorkflowStatuses.value = []
    form.value.default_workflow_status_id = null
  }
}

// Checklist form helpers
function addChecklistItem() {
  if (!newChecklistItemTitle.value.trim()) return
  checklistForm.value.items.push({ title: newChecklistItemTitle.value.trim() })
  newChecklistItemTitle.value = ''
}

function handleChecklistItemEnter(event) {
  if (!newChecklistItemTitle.value.trim()) {
    event.preventDefault()
    return
  }

  event.preventDefault()
  addChecklistItem()
  nextTick(() => {
    if (newChecklistItemInput.value?.focus) {
      newChecklistItemInput.value.focus()
    }
  })
}

function handleChecklistItemTab(event) {
  if (!newChecklistItemTitle.value.trim()) return

  event.preventDefault()
  addChecklistItem()
  nextTick(() => {
    if (newChecklistItemInput.value?.focus) {
      newChecklistItemInput.value.focus()
    }
  })
}

function cancelChecklistItemCreation() {
  newChecklistItemTitle.value = ''
  if (newChecklistItemInput.value?.blur) {
    newChecklistItemInput.value.blur()
  }
}

function removeChecklistItem(index) {
  checklistForm.value.items.splice(index, 1)
}

function clearChecklist() {
  checklistForm.value = { items: [] }
  newChecklistItemTitle.value = ''
}

function checklistItemsPayload() {
  const items = [...checklistForm.value.items]
  const pendingTitle = newChecklistItemTitle.value.trim()

  if (pendingTitle) {
    items.push({ title: pendingTitle })
  }

  return items
    .filter(item => item.title.trim())
    .map((item, index) => ({
      title: item.title.trim(),
      position: index + 1,
    }))
}

const canSubmit = computed(() => {
  return form.value.task_title.trim().length > 0
})

// Show task description preview
async function showTaskDescriptionPreview() {
  if (!form.value.task_description?.trim()) return

  // Ensure URLs have schemas before preview
  ensureTaskDescriptionUrlSchemas()

  // Build task URL helper for preview
  const buildTaskUrl = (taskId, taskProjectId) => {
    if (taskProjectId) {
      return `/projects/${taskProjectId}/tasks/${taskId}`
    }
    return '#'
  }

  // Use the shared read renderer for consistency with task/comment descriptions.
  taskDescriptionPreviewContent.value = telegramMarkdownToHtml(form.value.task_description, {
    processTaskMentions: true,
    taskMap: {}, // Empty map for templates - task mentions won't be clickable
    buildTaskUrl: buildTaskUrl,
  })

  showTaskDescriptionPreviewModal.value = true
}

function ensureTaskDescriptionUrlSchemas() {
  if (!form.value.task_description) return

  // Add http:// to URLs that don't have a schema
  form.value.task_description = form.value.task_description.replace(
    /(^|[^/])(www\.[^\s]+)/g,
    '$1http://$2'
  )
}

function handleClose(event) {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  emit('update:modelValue', false)
  emit('close')
}

function handleSubmit(event) {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  if (!canSubmit.value) return

  // Prepare form data for submission
  // Convert snake_case to camelCase for GraphQL mutation
  const submitData = {
    description: form.value.description?.trim() || null,
    taskTitle: form.value.task_title.trim(), // Required field - don't remove even if empty
    taskDescription: form.value.task_description?.trim() || null,
    defaultAssigneeId: form.value.default_assignee_id || null,
    defaultPriority: form.value.default_priority ? form.value.default_priority.toUpperCase() : null,
    defaultTaskCategoryId: form.value.default_task_category_id || null,
    defaultWorkflowStatusId: form.value.default_workflow_status_id || null,
    defaultParentTaskId: form.value.default_parent_task_id || null,
    defaultLabels: form.value.default_labels.length > 0 ? form.value.default_labels : null,
    defaultPersonalLabels:
      form.value.default_personal_labels.length > 0 ? form.value.default_personal_labels : null,
    defaultConfidential:
      form.value.default_confidential === true
        ? true
        : form.value.default_confidential === false
          ? false
          : null,
    // Include checklistData if it has items (always allow saving, even for personal templates)
    // It will only be applied when creating a task if the project has checklists enabled
    checklistData:
      checklistItemsPayload().length > 0
        ? {
            title: null,
            items: checklistItemsPayload(),
          }
        : null,
    attachmentDraftId: attachmentDraftId.value || null,
    existingAttachmentIds: props.scope === 'project' ? selectedExistingAttachmentIds.value : null,
  }

  // Remove null/undefined values, but keep required fields (taskTitle)
  // Also keep boolean false values (they are valid, not null)
  // Keep checklistData if explicitly set (even if null, to allow clearing in updates)
  // Required fields should never be removed even if null/undefined
  Object.keys(submitData).forEach(key => {
    const isRequired = key === 'taskTitle'
    const isChecklistData = key === 'checklistData'
    const value = submitData[key]
    // Keep required fields, keep boolean false, keep checklistData if explicitly set, remove null/undefined
    if (
      !isRequired &&
      !isChecklistData &&
      value !== false &&
      (value === null || value === undefined)
    ) {
      delete submitData[key]
    }
  })

  emit('submit', submitData)
}
</script>

<style scoped>
/* Modal styles */
/* Component-specific: z-index, padding, and max-width overrides */
.modal-overlay {
  z-index: 10000;
  padding: 20px;
}

.modal-content {
  max-width: 700px;
}

.form-row {
  margin-bottom: 16px;
}

.form-row-split {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.form-row label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.form-row input[type='text'],
.form-row textarea,
.form-row select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
}

.form-row textarea {
  resize: vertical;
  min-height: 80px;
}

.form-row select:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 500;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 500;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.error {
  color: #c62828;
  background: #ffebee;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ef5350;
}

.muted {
  color: #999;
  font-size: 14px;
}

.checklist-items-form {
  margin-top: 8px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.checklist-item-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 4px;
}

.checklist-item-row input {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.btn-icon-small {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.btn-icon-small:hover {
  background-color: #f0f0f0;
}

.btn-sm {
  padding: 6px 12px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-sm:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-sm:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
